<script setup lang="ts">
/**
 * AG Grid Vue component cell renderer for the From PO / To PO pill columns.
 *
 * Renders the PO number as a coloured pill and wraps it in an AppPopover
 * that shows warehouse name, cubes before, and cubes after on hover.
 *
 * Pass variant via cellRendererParams:
 *   { cellRenderer: PoPillCellRenderer, cellRendererParams: { variant: "from" } }
 */

import AppPopover from "@/components/AppPopover.vue";
import type { MovementGridRow } from "@/redistribution/models/MovementGridRow.ts";
import { useAppStore } from "@/stores/app.ts";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface CellRendererParams {
  value: string;
  data: MovementGridRow;
  /** Injected via cellRendererParams in the column definition. */
  variant: "from" | "to";
}

const props = defineProps<{ params: CellRendererParams }>();

const variant = computed(() => props.params.variant);
const poNumber = computed(() => props.params.value);
const row = computed(() => props.params.data);

const { warehouseOptions } = storeToRefs(useAppStore());

function warehouseName(
  division: string | null | undefined,
  warehouseId: number | null | undefined
): string {
  if (warehouseId == null) return "—";
  // warehouseOptions.value has keys like "FL_201"
  const key = division
    ? `${division}_${String(warehouseId)}`
    : String(warehouseId);
  const opt = warehouseOptions.value.find((o) => o.value === key);
  return opt?.label ?? String(warehouseId);
}

const displayWarehouse = computed(() =>
  variant.value === "from"
    ? warehouseName(row.value.fromDivision, row.value.fromWarehouse)
    : warehouseName(row.value.toDivision, row.value.toWarehouse)
);

const cubesBefore = computed(() =>
  variant.value === "from" ? row.value.fromCubesBefore : row.value.toCubesBefore
);

const cubesAfter = computed(() =>
  variant.value === "from" ? row.value.fromCubesAfter : row.value.toCubesAfter
);

const popoverPlacement = computed<"right">(() => "right");

const popoverTitle = computed(() => poNumber.value);

const pillClass = computed(() =>
  variant.value === "from"
    ? "item-movements-from-pill"
    : "item-movements-to-pill"
);

function fmt(val: number | null | undefined): string {
  return val == null ? "—" : val.toFixed(1);
}
</script>

<template>
  <AppPopover :title="popoverTitle" :placement="popoverPlacement" :width="220">
    <template #trigger>
      <span :class="pillClass">{{ poNumber }}</span>
    </template>

    <table class="po-popover-table">
      <tbody>
        <tr>
          <td class="po-popover-label">Warehouse</td>
          <td class="po-popover-value">{{ displayWarehouse }}</td>
        </tr>
        <tr>
          <td class="po-popover-label">Cubes Before</td>
          <td class="po-popover-value">{{ fmt(cubesBefore) }}</td>
        </tr>
        <tr>
          <td class="po-popover-label">Cubes After</td>
          <td class="po-popover-value">{{ fmt(cubesAfter) }}</td>
        </tr>
      </tbody>
    </table>
  </AppPopover>
</template>
